import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const LOGIN_API_URL = `${API_BASE_URL}/api/auth/login`;
const SSO_LOGIN_API_URL = `${API_BASE_URL}/api/auth/sso/login`;
const SSO_EXCHANGE_API_URL = `${API_BASE_URL}/api/auth/sso/exchange`;

type AuthUser = {
  id?: number | string;
  email?: string;
  role_name?: string;
  [key: string]: unknown;
};

type LoginPayload = {
  success?: boolean;
  message?: string;
  user?: AuthUser;
};

type LoginArgs = {
  email: string;
  password: string;
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ssoSubmitting, setSsoSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const persistAuth = (payload: LoginPayload) => {
    const roleName = payload?.user?.role_name || "";
    const isAdmin = String(roleName).toLowerCase() === "admin";

    localStorage.setItem("authUser", JSON.stringify(payload?.user || {}));
    localStorage.setItem("isAdminUser", String(isAdmin));
    localStorage.setItem("userEmail", payload?.user?.email || "");
    localStorage.setItem("userRole", roleName);
    localStorage.setItem("userId", String(payload?.user?.id || ""));
  };

  const performLogin = async ({ email, password }: LoginArgs) => {
    setSubmitting(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        LOGIN_API_URL,
        { email, password },
        { withCredentials: true },
      );
      const payload = response.data as LoginPayload;

      persistAuth(payload);
      navigate("/", { replace: true });
    } catch (error) {
      const statusCode = (error as { response?: { status?: unknown } })?.response?.status;

      if (statusCode === 400 || statusCode === 401) {
        setErrorMessage("Invalid email or password.");
      } else if (statusCode === 403) {
        setErrorMessage("Access denied.");
      } else {
        setErrorMessage("Unable to login. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void performLogin({ email, password });
  };

  const handleSsoLogin = () => {
    setErrorMessage("");
    setStatusMessage("");
    setSsoSubmitting(true);
    window.location.assign(SSO_LOGIN_API_URL);
  };

  useEffect(() => {
    if (location.pathname !== "/auth/callback") {
      if (location.pathname === "/auth/pending") {
        setErrorMessage("");
        setStatusMessage("Please reach out to the Admin for access.");
      }

      return;
    }

    const searchParams = new URLSearchParams(location.search || "");
    const code = searchParams.get("code");

    if (!code) {
      setErrorMessage("SSO callback code is missing.");
      setStatusMessage("");
      return;
    }

    const exchangeLockKey = `ssoExchange:${code}`;
    const existingLock = sessionStorage.getItem(exchangeLockKey);

    if (existingLock === "processing" || existingLock === "done") {
      return;
    }

    sessionStorage.setItem(exchangeLockKey, "processing");

    const exchangeCode = async () => {
      setSubmitting(true);
      setSsoSubmitting(true);
      setErrorMessage("");
      setStatusMessage("Completing SSO login...");

      try {
        const response = await axios.post(
          SSO_EXCHANGE_API_URL,
          { code },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const payload = response.data as LoginPayload;

        if (!payload?.success) {
          setErrorMessage(payload?.message || "SSO exchange failed.");
          setStatusMessage("");
          return;
        }

        persistAuth(payload);
        sessionStorage.setItem(exchangeLockKey, "done");
        navigate("/", { replace: true });
      } catch (error) {
        const fallback = "Unable to complete SSO login. Please try again.";
        sessionStorage.removeItem(exchangeLockKey);
        const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
          ?.message;
        setErrorMessage(typeof message === "string" ? message : fallback);
        setStatusMessage("");
      } finally {
        setSubmitting(false);
        setSsoSubmitting(false);
      }
    };

    void exchangeCode();
  }, [location.pathname, location.search, navigate]);

  return (
    <div className="min-h-screen w-full bg-[#f4fbf6] relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-10 h-72 w-72 rounded-full bg-emerald-100/80 blur-3xl"></div>
        <div className="absolute top-24 right-10 h-64 w-64 rounded-full bg-sky-100/70 blur-3xl"></div>
        <div className="absolute bottom-8 left-1/3 h-56 w-56 rounded-full bg-amber-100/70 blur-3xl"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Welcome</h1>
          <p className="mt-2 text-gray-600">Sign in</p>
        </div>

        <div className="mx-auto max-w-xl">
          {errorMessage && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {errorMessage}
            </div>
          )}
          {statusMessage && (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
              {statusMessage}
            </div>
          )}

          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_12px_30px_rgba(16,24,40,0.08)]">
            <div className="mb-5 flex items-center gap-2 text-[15px] font-semibold text-gray-800">
              <span className="inline-block h-4 w-[3px] rounded-full bg-lime-500"></span>
              <span>Login</span>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-200"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-800"
              >
                {submitting ? "Signing in..." : "Login"}
              </button>

              <button
                type="button"
                disabled={submitting || ssoSubmitting}
                onClick={handleSsoLogin}
                className="inline-flex w-full items-center justify-center rounded-xl border border-emerald-700 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {ssoSubmitting ? "Redirecting to SSO..." : "SSO Login"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;